import React, { useContext } from 'react'
import { StatusBarContext } from './StatusBarContext'
import TargetColorBox from './TargetColorBox'
import styles from './../css/TargetSummaryBar.module.css'

function badgeClass(max, exceedsMax, belowMin) {
    if (max === 0) return styles.neutral;
    if (exceedsMax || belowMin) return styles.warn;
    return styles.ok;
}

function arrowSymbol(exceedsMax, belowMin) {
    if (exceedsMax) return ' ▼';
    if (belowMin) return ' ▲';
    return ' ✓';
}

function TargetSummaryBar() {
    const {
        activeTargets,
        statusTotals,
        startTotalNumberOfCallsForTerritory,
        totalChangesInTerritory,
        totalUpgradesInTerritory,
        totalDowngradesInTerritory,
        totalProfilesInTerritory,
        totalPlannedCallsForTerritory,
        maxChangesPerTerritory,
        minChangesPerTerritory,
        maxUpgradesPerTerritory,
        maxDowngradesPerTerritory,
        maxPlannedCallsPerTerritory,
        minPlannedCallsPerTerritory,
        exceedsMaxChangesPerTerritory,
        belowMinChangesPerTerritory,
        exceedsMaxUpgradesPerTerritory,
        exceedsMaxDowngradesPerTerritory,
        exceedsMaxPlannedCallsPerTerritory,
        belowMinPlannedCallsPerTerritory,
        perTargetLimits,
        perTargetLimitData,
        perCallLimitData,
        perUpgradeLimitData,
        perDowngradeLimitData,
        startCallTotals,
        exercise,
    } = useContext(StatusBarContext);

    const totalCalls = totalPlannedCallsForTerritory();

    const perTargetLimitsEntries = (perTargetLimits && perTargetLimits !== 0) ? Object.values(perTargetLimits) : [];
    // Use raw exercise config values — not computed limits — so guardrail columns are visible
    // even when CURRENT_TOTAL_* is 0 (e.g. a substitute rep on a vacant territory whose
    // STATUS row was seeded with zeroes instead of real counts).
    const hasTargetGuardrails = (exercise?.MAX_CHANGES_PER_TERRITORY > 0) || (exercise?.MIN_CHANGES_PER_TERRITORY > 0)
        || perTargetLimitsEntries.some(t => (t.maxChanges ?? 0) > 0 || (t.minRequired ?? 0) > 0);
    const hasCallGuardrails = (exercise?.MAX_CALLS_PER_TERRITORY > 0) || (exercise?.MIN_CALLS_PER_TERRITORY > 0)
        || perTargetLimitsEntries.some(t => (t.maxCalls ?? 0) > 0 || (t.minCalls ?? 0) > 0);
    const hasUpgradeGuardrails = (exercise?.MAX_UPGRADES_PER_TERRITORY > 0)
        || perTargetLimitsEntries.some(t => (t.maxUpgrades ?? 0) > 0);
    const hasDowngradeGuardrails = (exercise?.MAX_DOWNGRADES_PER_TERRITORY > 0)
        || perTargetLimitsEntries.some(t => (t.maxDowngrades ?? 0) > 0);

    const gridTemplateColumns = `48px${hasTargetGuardrails ? ' 1fr' : ''}${hasCallGuardrails ? ' 1fr' : ''}${hasUpgradeGuardrails ? ' 1fr' : ''}${hasDowngradeGuardrails ? ' 1fr' : ''}`;

    // Compute the absolute min/max label for target change guardrails
    const targetGuardrailLabel = (targetType, currentCount) => {
        if (!perTargetLimits || perTargetLimits === 0 || !perTargetLimits[targetType]) return null;
        const raw = perTargetLimits[targetType];
        // minRequired: floor on how many must REMAIN (size guard)
        const minVal = raw.minRequired > 0 ? currentCount - Math.ceil((raw.minRequired / 100) * currentCount) : null;
        // maxChanges: cap on how many can move AWAY from this type (outflow churn guard)
        const maxVal = raw.maxChanges > 0 ? Math.ceil((raw.maxChanges / 100) * currentCount) : null;
        if (minVal === null && maxVal === null) return null;
        return [minVal !== null ? `remaining floor: ${minVal}` : '', maxVal !== null ? `max reclassified: ${maxVal}` : ''].filter(Boolean).join('  ');
    };

    // Compute the absolute min/max label for planned call guardrails
    const callGuardrailLabel = (targetType) => {
        if (!perTargetLimits || perTargetLimits === 0 || !perTargetLimits[targetType]) return null;
        const startCalls = startCallTotals?.[targetType] ?? 0;
        const raw = perTargetLimits[targetType];
        const minVal = raw.minCalls > 0 ? startCalls - Math.ceil((raw.minCalls / 100) * startCalls) : null;
        const maxVal = raw.maxCalls > 0 ? startCalls + Math.ceil((raw.maxCalls / 100) * startCalls) : null;
        if (minVal === null && maxVal === null) return null;
        return [minVal !== null ? `min: ${minVal}` : '', maxVal !== null ? `max: ${maxVal}` : ''].filter(Boolean).join('  ');
    };

    return (
        <div className={styles.grid} style={{ gridTemplateColumns }}>
            {/* Column headers */}
            <div className={styles.headerCell}></div>
            {hasTargetGuardrails && <div className={styles.headerCell}>Target Change Guardrails</div>}
            {hasCallGuardrails && <div className={styles.headerCell}>Planned Call Guardrails</div>}
            {hasUpgradeGuardrails && <div className={styles.headerCell}>Upgrade Guardrails</div>}
            {hasDowngradeGuardrails && <div className={styles.headerCell}>Downgrade Guardrails</div>}

            {/* Per-target rows */}
            {activeTargets.map(targetType => {
                const totals = statusTotals[targetType] || { current: 0, final: 0 };
                const tld = perTargetLimitData[targetType];
                const cld = perCallLimitData[targetType];

                return (
                    <React.Fragment key={targetType}>
                        <div className={styles.colorCell}>
                            <TargetColorBox target={targetType} small />
                        </div>
                        {hasTargetGuardrails && <div className={styles.badgeCell}>
                            {tld ? (() => {
                                const tip = targetGuardrailLabel(targetType, totals.current);
                                return (
                                    <div className={tip ? styles.tooltipWrap : undefined}>
                                        <span className={`${styles.badge} ${badgeClass(tld.maxChanges, tld.exceedsMax, tld.belowMin)}`}>
                                            {tld.maxChanges > 0
                                                ? `${tld.outflowCount}/${tld.maxChanges}${arrowSymbol(tld.exceedsMax, tld.belowMin)}`
                                                : '♾️'}
                                        </span>
                                        {tip && <div className={styles.tooltip}>{tip}</div>}
                                    </div>
                                );
                            })() : (() => {
                                const tip = targetGuardrailLabel(targetType, totals.current);
                                if (!tip) return <span className={`${styles.badge} ${styles.neutral}`}>♾️</span>;
                                return <span className={`${styles.badge} ${styles.neutral}`}>{tip}</span>;
                            })()}
                        </div>}
                        {hasCallGuardrails && <div className={styles.badgeCell}>
                            {cld ? (() => {
                                const tip = callGuardrailLabel(targetType);
                                return (
                                    <div className={tip ? styles.tooltipWrap : undefined}>
                                        <span className={`${styles.badge} ${badgeClass(cld.maxCalls, cld.exceedsMaxCalls, cld.belowMinCalls)}`}>
                                            {cld.maxCalls > 0
                                                ? `${cld.currentCallCount}/${cld.maxCalls}${arrowSymbol(cld.exceedsMaxCalls, cld.belowMinCalls)}`
                                                : '♾️'}
                                        </span>
                                        {tip && <div className={styles.tooltip}>{tip}</div>}
                                    </div>
                                );
                            })() : (() => {
                                const tip = callGuardrailLabel(targetType);
                                if (!tip) return <span className={`${styles.badge} ${styles.neutral}`}>♾️</span>;
                                return <span className={`${styles.badge} ${styles.neutral}`}>{tip}</span>;
                            })()}
                        </div>}
                        {hasUpgradeGuardrails && <div className={styles.badgeCell}>
                            {(() => {
                                const uld = (perUpgradeLimitData ?? {})[targetType];
                                if (uld) return (
                                    <span className={`${styles.badge} ${badgeClass(uld.maxUpgrades, uld.exceedsMax, false)}`}>
                                        {`${uld.upgradeCount}/${uld.maxUpgrades}${arrowSymbol(uld.exceedsMax, false)}`}
                                    </span>
                                );
                                return <span className={`${styles.badge} ${styles.neutral}`}>♾️</span>;
                            })()}
                        </div>}
                        {hasDowngradeGuardrails && <div className={styles.badgeCell}>
                            {(() => {
                                const dld = (perDowngradeLimitData ?? {})[targetType];
                                if (dld) return (
                                    <span className={`${styles.badge} ${badgeClass(dld.maxDowngrades, dld.exceedsMax, false)}`}>
                                        {`${dld.downgradeCount}/${dld.maxDowngrades}${arrowSymbol(dld.exceedsMax, false)}`}
                                    </span>
                                );
                                return <span className={`${styles.badge} ${styles.neutral}`}>♾️</span>;
                            })()}
                        </div>}
                    </React.Fragment>
                );
            })}

            {/* Territory totals row */}
            <div className={styles.separator} />
            <div className={styles.colorCell}>
                <span className={styles.totalLabel}>TERRITORY</span>
            </div>
            {hasTargetGuardrails && <div className={styles.badgeCell}>
                <span className={`${styles.badge} ${badgeClass(maxChangesPerTerritory, exceedsMaxChangesPerTerritory, belowMinChangesPerTerritory)}`}>
                    {maxChangesPerTerritory > 0
                        ? `${totalChangesInTerritory}/${maxChangesPerTerritory}${arrowSymbol(exceedsMaxChangesPerTerritory, belowMinChangesPerTerritory)}`
                        : '♾️'}
                </span>
            </div>}
            {hasCallGuardrails && <div className={styles.badgeCell}>
                <span className={`${styles.badge} ${badgeClass(maxPlannedCallsPerTerritory, exceedsMaxPlannedCallsPerTerritory, belowMinPlannedCallsPerTerritory)}`}>
                    {maxPlannedCallsPerTerritory > 0
                        ? `${totalCalls}/${maxPlannedCallsPerTerritory}${arrowSymbol(exceedsMaxPlannedCallsPerTerritory, belowMinPlannedCallsPerTerritory)}`
                        : '♾️'}
                </span>
            </div>}
            {hasUpgradeGuardrails && <div className={styles.badgeCell}>
                <span className={`${styles.badge} ${badgeClass(maxUpgradesPerTerritory, exceedsMaxUpgradesPerTerritory, false)}`}>
                    {maxUpgradesPerTerritory > 0
                        ? `${totalUpgradesInTerritory}/${maxUpgradesPerTerritory}${arrowSymbol(exceedsMaxUpgradesPerTerritory, false)}`
                        : '♾️'}
                </span>
            </div>}
            {hasDowngradeGuardrails && <div className={styles.badgeCell}>
                <span className={`${styles.badge} ${badgeClass(maxDowngradesPerTerritory, exceedsMaxDowngradesPerTerritory, false)}`}>
                    {maxDowngradesPerTerritory > 0
                        ? `${totalDowngradesInTerritory}/${maxDowngradesPerTerritory}${arrowSymbol(exceedsMaxDowngradesPerTerritory, false)}`
                        : '♾️'}
                </span>
            </div>}
        </div>
    );
}

export default TargetSummaryBar
